import { useState } from 'react';
import { Link } from 'react-router-dom';
import { projectList } from '@/mocks/projects';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

const PortfolioPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'Web', 'Mobile', 'Branding', 'Enterprise'];

  const filteredProjects = activeFilter === 'All'
    ? projectList
    : projectList.filter(p => p.category === activeFilter);

  const portfolioJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Our Portfolio — BitNinja Technologies",
    "description": "Explore BitNinja Technologies' complete portfolio of web development, mobile apps, branding, and enterprise IT projects delivered for clients worldwide.",
    "url": "https://bitninjatech.com/portfolio",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": filteredProjects.map((project, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "CreativeWork",
          "name": project.title,
          "url": `https://bitninjatech.com/portfolio/${project.slug}`,
          "description": project.description,
          "image": project.image
        }
      }))
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title="Portfolio | BitNinja Technologies"
        description="Explore our complete portfolio of web development, mobile apps, branding, and enterprise IT projects. 50+ successful projects delivered for clients worldwide."
        canonicalUrl="https://bitninjatech.com/portfolio"
        jsonLd={portfolioJsonLd}
      />
      <Navbar />
      <section className="pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                    <i className="ri-briefcase-line text-xs"></i>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">
                    All Projects
                  </span>
                </div>
                <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight text-foreground-900">
                  Our Complete{' '}
                  <span className="block text-primary-500">Portfolio.</span>
                </h1>
              </div>

              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-widest border transition-all cursor-pointer whitespace-nowrap ${
                      activeFilter === filter
                        ? 'bg-foreground-900 text-background-50 border-foreground-900'
                        : 'bg-background-50 text-foreground-700 border-background-300 hover:border-foreground-900'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <Link
                  to={`/portfolio/${project.slug}`}
                  key={project.slug}
                  className="group bg-background-100 border border-background-200 rounded-2xl overflow-hidden flex flex-col hover:bg-background-50 hover:border-foreground-900/30 transition-all"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      alt={project.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                      src={project.image}
                    />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-900 text-[10px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                      {project.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-3 flex items-center gap-3">
                      <span>{project.client}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span>{project.date}</span>
                    </div>
                    <h4 className="font-display text-xl font-bold text-foreground-900 leading-snug mb-3 group-hover:text-primary-500 transition-colors">
                      {project.title}
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                      {project.description}
                    </p>
                    <div className="mt-5 pt-5 border-t border-background-200">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">
                        View Project <i className="ri-arrow-right-up-line text-base"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PortfolioPage;